//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { AggregatedStatsDetails } from '@apis/rights-insights/rightsInsightsApiModel';
import { DateRangeAlias } from '@components/date-range-picker/dateRangePickerModels';

export const StatTitles = {
  totalRequested: 'Total Requested',
  totalApproved: 'Total Approved',
  totalRejected: 'Total Rejected',
  totalPending: 'Total Pending',
} as const satisfies Record<keyof typeof statMappings, string>;

interface StatMapping {
  valueKey: keyof AggregatedStatsDetails;
  changeKey: keyof AggregatedStatsDetails;
  title: string;
  statTooltip: string;
}

export const statMappings = {
  totalRequested: {
    valueKey: 'requested',
    title: 'Requested',
    statTooltip:
      'The total number of rights requests sent in the selected timeframe.',
    changeKey: 'previousRequested',
  },
  totalApproved: {
    valueKey: 'approved',
    title: 'Approved',
    statTooltip:
      'The number of rights requests that were approved by creators during the selected timeframe.',
    changeKey: 'previousApproved',
  },
  totalRejected: {
    valueKey: 'rejected',
    title: 'Rejected',
    statTooltip:
      'The number of rights Requests that expired due to no response',
    changeKey: 'previousRejected',
  },
  totalPending: {
    valueKey: 'pending',
    title: 'Pending',
    statTooltip:
      'The number of rights requests that are awaiting creator action during the selected timeframe.',
    changeKey: 'previousPending',
  },
} as const satisfies Record<string, StatMapping>;

export const RIGHTS_INSIGHTS_DATE_RANGE_ALIASES: DateRangeAlias[] = [
  'last7Days',
  'last30Days',
  'last3Months',
  'last1Year',
  'allTime',
];

export enum RightsInsightsCharts {
  PercentageApproved = 'PercentageApproved',
  TotalApproved = 'TotalApproved',
  TotalRequests = 'TotalRequests',
}

type RightsInsightsChartLabels = {
  [key in RightsInsightsCharts]: string;
};

export const RightsInsightsChartLabels: RightsInsightsChartLabels = {
  [RightsInsightsCharts.PercentageApproved]: 'Percentage Approved',
  [RightsInsightsCharts.TotalApproved]: 'Total Approved',
  [RightsInsightsCharts.TotalRequests]: 'Total Requests',
};

type RightsInsightsChartRadioButtonIds = {
  [key in RightsInsightsCharts]: string;
};

export const RightsInsightsChartRadioButtonIds: RightsInsightsChartRadioButtonIds =
  {
    [RightsInsightsCharts.PercentageApproved]: 'percentage-approved',
    [RightsInsightsCharts.TotalApproved]: 'total-approved',
    [RightsInsightsCharts.TotalRequests]: 'total-requests',
  };

export const RIGHTS_MESSAGES_PERFORMANCE_TABLE_COUNT = 8;
export const RIGHTS_MESSAGES_PERFORMANCE_TABLE_METRICS_COLUMN_COUNT = 4;

export const TABLE_ACTION_BUTTONS_WIDTH = 64;

export const RightsMessagesPerformanceMetrics = {
  RequestMessage: 'Request Message',
  Requested: 'Requested',
  Approved: 'Approved',
  ApprovalRate: 'Approval Rate',
} as const;

export type RightsMessagesPerformanceMetricsKeys =
  keyof typeof RightsMessagesPerformanceMetrics;

export const RightsMessagePerformancetableHeadersTitles: Record<
  RightsMessagesPerformanceMetricsKeys,
  string
> = {
  RequestMessage: 'REQUEST MESSAGE',
  Requested: 'REQUESTED',
  Approved: 'APPROVED',
  ApprovalRate: 'APPROVAL RATE',
};

export const RightsMessagePerformanceTableHeaders: Record<
  RightsMessagesPerformanceMetricsKeys,
  string
> = {
  RequestMessage: 'Request Message',
  Requested: 'Requested',
  Approved: 'Approved',
  ApprovalRate: 'Approval Rate',
};

export interface TransformedLineChartData {
  categories: string[];
  seriesData: SeriesData[];
  isRate?: boolean;
}

export interface SeriesData {
  color: string;
  series: number[];
  label: string;
}

export type RightsInsightsTransformedData = {
  [key in RightsInsightsCharts]: TransformedLineChartData;
};
